import { z } from 'zod'

const BONUS_TYPE_VALUES = ['performance', 'attendance', 'taxable', 'non_taxable'] as const

/** Matches backend validation message for wrong payroll period. */
export const BONUS_PAYROLL_MONTH_ERROR_MESSAGE =
  'Bonus can only be created for the current payroll month'

/** Returns { year, month } based on browser local time. Used to pre-fill the form. */
export function getDefaultPayrollYearMonth(): { year: number; month: number } {
  const now = new Date()
  return { year: now.getFullYear(), month: now.getMonth() + 1 }
}

export const createBonusSchema = z
  .object({
    userId: z.string().min(1, 'Employee is required'),
    year: z
      .number({ message: 'Year must be a number' })
      .min(2000, 'Year must be 2000 or later')
      .max(2100, 'Year must be 2100 or earlier'),
    month: z
      .number({ message: 'Month must be a number' })
      .min(1, 'Month must be between 1 and 12')
      .max(12, 'Month must be between 1 and 12'),
    bonusType: z.enum(BONUS_TYPE_VALUES, { message: 'Bonus type is required' }),
    amount: z
      .number({ message: 'Amount must be a number' })
      .min(0.01, 'Amount must be at least 0.01'),
    currency: z.string().min(1, 'Currency is required'),
    reason: z
      .string()
      .optional()
      .refine((val) => val === undefined || val === '' || val.length >= 1, {
        message: 'Reason must not be empty if provided',
      }),
  })
  .superRefine((data, ctx) => {
    const { year: expectedYear, month: expectedMonth } = getDefaultPayrollYearMonth()
    if (data.year !== expectedYear || data.month !== expectedMonth) {
      const issue = {
        code: z.ZodIssueCode.custom,
        message: BONUS_PAYROLL_MONTH_ERROR_MESSAGE,
      } as const
      ctx.addIssue({ ...issue, path: ['year'] })
      ctx.addIssue({ ...issue, path: ['month'] })
    }
  })

export type CreateBonusFormData = z.infer<typeof createBonusSchema>
